"use client";

import { useRef, type ReactElement } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { cn } from "@/lib/utils";
import type { LadderLineSummary } from "@/lib/odds-screen/live/derive";
import type { MarketSpec } from "@/lib/odds-screen/live/types";
import { cellLineLabel, outcomeDisplayOrder, trimLine } from "./labels";

export type LineMenuOption = { readonly line: number; readonly label: string; readonly books: number; readonly isMain: boolean };

function lineLabel(spec: MarketSpec, line: number): string {
  return spec.line === "spread" ? outcomeDisplayOrder(spec).map((outcome) => cellLineLabel(spec, line, outcome)).join(" / ") : trimLine(line);
}

function focusSelected(node: HTMLButtonElement | null): void {
  if (node === null) return;
  node.focus({ preventScroll: true });
  const menu = node.offsetParent;
  if (menu instanceof HTMLElement) menu.scrollTop = node.offsetTop - (menu.clientHeight - node.offsetHeight) / 2;
}

export function LineMenu({
  trigger, open, options, selectedLine, pinned = false, tall = false, className, onOpenChange, onPick,
}: {
  trigger: ReactElement;
  open: boolean;
  options: readonly LineMenuOption[] | null;
  selectedLine: number | null;
  pinned?: boolean;
  tall?: boolean;
  className?: string;
  onOpenChange: (open: boolean) => void;
  onPick: (option: LineMenuOption | null) => void;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const escape = pinned && options !== null && !options.some((option) => option.isMain);
  const optionClass = (selected: boolean) => cn(
    "flex w-full items-center gap-2 rounded-md px-2.5 text-[13px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40",
    tall ? "h-11" : "h-8",
    selected ? "bg-primary/10 text-primary" : "text-zinc-300 hover:bg-white/[0.06] hover:text-foreground",
  );
  const pick = (option: LineMenuOption | null) => { onPick(option); onOpenChange(false); };

  const onListKeyDown = (event: React.KeyboardEvent) => {
    const step = event.key === "ArrowDown" ? 1 : event.key === "ArrowUp" ? -1 : 0;
    if (step === 0) return;
    event.preventDefault();
    const items = Array.from(listRef.current?.querySelectorAll<HTMLElement>("[role=option]") ?? []);
    const current = items.indexOf(document.activeElement as HTMLElement);
    items[(current + step + items.length) % items.length]?.focus();
  };

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <PopoverPrimitive.Trigger asChild>{trigger}</PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          aria-label="Change line"
          side="bottom"
          align="start"
          sideOffset={6}
          collisionPadding={8}
          onOpenAutoFocus={(event) => event.preventDefault()}
          className={cn("relative z-30 max-h-[360px] w-52 origin-[var(--radix-popover-content-transform-origin)] overflow-y-auto rounded-lg border border-surface-line bg-surface-overlay p-1 shadow-xl outline-none animate-in fade-in-0 zoom-in-95 duration-150 motion-reduce:animate-none", className)}
        >
          {options === null ? (
            <p className="px-2.5 py-2 text-[12px] text-zinc-500">Loading lines…</p>
          ) : options.length < 2 && !pinned ? (
            <p className="px-2.5 py-2 text-[12px] text-zinc-500">No other lines</p>
          ) : (
            <div ref={listRef} role="listbox" aria-label="Lines" onKeyDown={onListKeyDown}>
              {escape && (
                <button type="button" role="option" aria-selected={false} onClick={() => pick(null)} className={optionClass(false)}>
                  <span>Main line</span>
                </button>
              )}
              {options.map((option) => {
                const selected = option.line === selectedLine;
                const books = `${option.books} ${option.books === 1 ? "book" : "books"}`;
                return (
                  <button
                    key={option.line}
                    ref={selected ? focusSelected : undefined}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    aria-label={`${option.label}, ${books}${option.isMain ? ", main line" : ""}`}
                    onClick={() => pick(option)}
                    className={optionClass(selected)}
                  >
                    <span className="data-num whitespace-nowrap">{option.label}</span>
                    {option.isMain && <span className="text-[10px] font-medium uppercase tracking-[0.06em] text-zinc-500">Main</span>}
                    <span className="data-num ml-auto text-[11px] text-zinc-500">{books}</span>
                  </button>
                );
              })}
            </div>
          )}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

export function LinePicker({
  spec, labels, pinned, open, summaries, selectedLine, onOpenChange, onPick,
}: {
  spec: MarketSpec;
  labels: readonly string[];
  pinned: boolean;
  open: boolean;
  summaries: readonly LadderLineSummary[] | null;
  selectedLine: number | null;
  onOpenChange: (open: boolean) => void;
  onPick: (line: number | null) => void;
}) {
  const options = summaries === null
    ? null
    : summaries.filter((entry) => entry.books > 0 || entry.isMain).map((entry) => ({ ...entry, label: lineLabel(spec, entry.line) }));
  return (
    <LineMenu
      open={open}
      options={options}
      selectedLine={selectedLine}
      pinned={pinned}
      onOpenChange={onOpenChange}
      onPick={(option) => onPick(option === null || option.isMain ? null : option.line)}
      trigger={
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={`Line ${labels.join(" / ")}, change line`}
          className={cn("flex w-full items-center gap-1 rounded-md px-1.5 hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40", open && "bg-white/[0.06]")}
        >
          <span className="flex min-w-0 flex-col gap-0.5">
            {labels.map((label, i) => (
              <span key={i} className={cn("data-num flex h-[26px] items-center text-[12px]", pinned ? "text-primary" : "text-zinc-400")}>{label}</span>
            ))}
          </span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="ml-auto flex-shrink-0 text-zinc-500"><path d="m6 9 6 6 6-6" /></svg>
        </button>
      }
    />
  );
}
